import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { CalendarBlank, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { ARTICLES, ArticleCTA } from "@/pages/blogArticles";
import { ShareButtons } from "@/components/ShareButtons";

const Author = () => (
  <div className="flex items-center gap-3 mb-6" data-testid="blog-author">
    <img src="/author.webp" alt="Vratislav Výšek — zakladatel TradeMind AI" className="w-10 h-10 rounded-full object-cover border-2 border-amber-500/60" loading="lazy" />
    <div>
      <div className="text-sm font-medium text-zinc-200">Vratislav Výšek</div>
      <div className="text-xs text-zinc-500">Zakladatel & CEO · VV Software</div>
    </div>
  </div>
);

const Shell = ({ lang, setLang, children }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-100" style={{ fontFamily: "Manrope, sans-serif" }}>
    <nav className="border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="blog-logo-home">
          <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-8 h-8 rounded-md" />
          <span className="font-semibold tracking-tight text-zinc-100">TradeMind <span className="text-blue-500">AI</span></span>
        </Link>
        <div className="flex items-center gap-4">
          <button onClick={() => setLang(lang === "cs" ? "en" : "cs")} className="text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-200 transition-colors">
            {lang === "cs" ? "EN" : "CS"}
          </button>
          <Link to="/register" className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors" data-testid="blog-nav-register">
            {lang === "cs" ? "Začít zdarma" : "Start free"}
          </Link>
        </div>
      </div>
    </nav>
    {children}
    <footer className="border-t border-zinc-900 py-8">
      <div className="max-w-3xl mx-auto px-6 text-xs text-zinc-600 space-y-1">
        <p>Provozovatel: Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno · Fyzická osoba zapsaná v živnostenském rejstříku · Neplátce DPH · vvsoftware@seznam.cz · +420 702 995 353</p>
        <p>Obchodování s pákovými produkty nese vysoké riziko ztráty. TradeMind AI poskytuje analytické nástroje, nikoli investiční poradenství.</p>
      </div>
    </footer>
  </div>
);

const ArticleDetail = ({ article, lang, setLang }) => {
  const { Content } = article;
  return (
    <Shell lang={lang} setLang={setLang}>
      <Seo
        title={`${article.title} — TradeMind AI`}
        description={article.description}
        canonical={`https://trademind.cz/blog/${article.slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          datePublished: article.dateISO,
          inLanguage: "cs",
          image: `https://trademind.cz${article.image}`,
          author: { "@type": "Person", name: "Vratislav Výšek", jobTitle: "Zakladatel & CEO, VV Software" },
          publisher: { "@type": "Organization", name: "TradeMind AI", logo: { "@type": "ImageObject", url: "https://trademind.cz/icons/icon-512.png" } },
          mainEntityOfPage: `https://trademind.cz/blog/${article.slug}`,
        }}
      />
      <article className="max-w-3xl mx-auto px-6 py-14" data-testid="blog-article">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-200 transition-colors mb-6" data-testid="blog-back">
          <ArrowLeft size={13} weight="bold" /> {lang === "cs" ? "Všechny články" : "All articles"}
        </Link>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4">
          <CalendarBlank size={14} weight="bold" /> {article.date} · {lang === "cs" ? "Novinky" : "News"}
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-6">{article.title}</h1>
        <Author />
        <div className="mb-8">
          <ShareButtons url={`https://trademind.cz/blog/${article.slug}`} text={article.title} lang={lang} />
        </div>
        <Content />
        <div className="mt-8">
          <ShareButtons url={`https://trademind.cz/blog/${article.slug}`} text={article.title} lang={lang} />
        </div>
        <ArticleCTA lang={lang} />
      </article>
    </Shell>
  );
};

const BlogList = ({ lang, setLang }) => (
  <Shell lang={lang} setLang={setLang}>
    <Seo
      title="Novinky a návody pro tradery — TradeMind AI blog"
      description="Návody na prop firm challenge, risk management, obchodní deník a trading disciplínu. Novinky z vývoje TradeMind AI — AI mentora pro tradery."
      canonical="https://trademind.cz/blog"
      schema={{
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "TradeMind AI Blog",
        url: "https://trademind.cz/blog",
        inLanguage: "cs",
        publisher: { "@type": "Organization", name: "TradeMind AI" },
      }}
    />
    <div className="max-w-3xl mx-auto px-6 py-14">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-3">
        {lang === "cs" ? "Novinky a návody" : "News & guides"}
      </h1>
      <p className="text-zinc-500 mb-10">
        {lang === "cs" ? "Prop trading, risk management a trading disciplína — bez omáčky." : "Prop trading, risk management and discipline — no fluff."}
      </p>
      <div className="space-y-6" data-testid="blog-list">
        {ARTICLES.map((a) => (
          <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`} className="block p-6 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40 transition-colors group">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">
              <CalendarBlank size={13} weight="bold" /> {a.date}
            </div>
            <h2 className="text-xl font-semibold text-zinc-100 tracking-tight mb-2 group-hover:text-amber-400 transition-colors">{a.title}</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-3">{a.description}</p>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-500">
              {lang === "cs" ? "Číst článek" : "Read article"} <ArrowRight size={12} weight="bold" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </Shell>
);

const Blog = () => {
  const { lang, setLang } = useT();
  const { slug } = useParams();
  if (slug) {
    const article = ARTICLES.find((a) => a.slug === slug);
    if (!article) return <Navigate to="/blog" replace />;
    return <ArticleDetail article={article} lang={lang} setLang={setLang} />;
  }
  return <BlogList lang={lang} setLang={setLang} />;
};

export default Blog;
